<script setup lang="ts">
import { computed, ref, ShallowRef, useTemplateRef } from 'vue';
import { ExamplePrompt, type Message, USER_ROLE } from '@/models';
import axios from 'axios';
import markdownit from 'markdown-it';
import { store } from '../store';
import { AntSubscription } from '@/antService';
import ToastService from '@/toastService';
import PromptExamples from './PromptExamples.vue';
import { i18n } from '@/assets/i18n';

// define props
const props = defineProps<{
  token: string;
  percent: number;
}>();

const md = markdownit({
  html: true,
  linkify: true,
  typographer: true
});

// define events
const emit = defineEmits(['onAnswer', 'onError']);

const chat: ShallowRef = useTemplateRef('chat-list');

const loadingPercent = ref(0);

const userInput = ref(false);
const chatInput = ref(null);
let usage = -1;
let inputTimeout = -1;

const loadingStyle = computed(() => {
  return (
    'filter: blur(' +
    (10 - Math.round(loadingPercent.value / 10)) +
    'px);opacity:' +
    (0.009 * loadingPercent.value + 0.1).toFixed(2) +
    ';'
  );
});

/**
 * Estimates the energy usage for an answer, based on the number of output tokens. Samsi et al. estimated the energy usage with 3 - 4 Joule per token, which equals to ~0.001 Wh
 * @see Paper       Samsi et al. (2023): From Words to Watts: Benchmarking the Energy Costs of Large Language Model Inference. https://doi.org/10.48550/arXiv.2310.03003
 * @see Blogpost    gpt-oss uses around 8.3 Wh per 100 output token, which seems way too high https://huggingface.co/blog/sasha/gpt-oss-energy
 * @see YouTube     https://www.youtube.com/watch?v=zjG9TkfWd0o&t=143s
 * @param tokens    number of output tokens of the answer
 * @param tps       token per seconds generated, defaults to 70
 * @returns         an estimation of energy (in Wh), based on the number of output tokens
 */
function estimateEnergyUsage(tokens: number, tps = 70) {
  // Mac Studio M3Max uses up to 480W. That is 0.1333 Wh per second in full load (480 / 3600).
  // however, he measures 120W: https://www.youtube.com/watch?v=zjG9TkfWd0o&t=143s TODO: own measurements
  // we use the Watt per Token stored in ENV setup
  const whPerSecond = Number(store.connection.WATT_PER_TOKEN) / 3600;
  // With gpt-oss:120b, it generates around 70 token/s.
  const whPerToken = whPerSecond / tps;
  const estimate = tokens * whPerToken;
  return estimate;
  // old samsi et al estimate: return (tokens * 3.5) / 3600; // 3.5 joules per token; 3600 J = 1 Wh
}

/**
 * Estimates the energy usage before knowing the answer
 * @returns         an estimation of energy (in Wh)
 */
function preEstimateUsage() {
  return usage > 1 ? usage : 1;
}

/**
 * Checks if a message is currently the last
 * @param index   the index of the message
 * @returns       TRUE if the message is the last in the array
 *                FALSE if there are more messages after it in the array
 */
function isLastMessage(index: number): boolean {
  return store.getVisibleMessages().length - index === 1;
}

/**
 * Sends a message to the model, with all previous messages as context
 */
function send() {
  if (store.textInput == '') return;

  const message: Message = {
    role: USER_ROLE.USER,
    content: store.textInput,
    loading: false
  };
  loadingPercent.value = 0;

  store.chatMessages.push(message);

  type API_BODY = {
    model: string;
    messages: Message[];
    reasoning?: {
      effort: string;
    };
    'background-tasks'?: {
      follow_up_generation: boolean;
    };
  };

  const body: API_BODY = {
    model: store.connection.MODEL,
    messages: [...store.chatMessages],
    reasoning: {
      effort: store.connection.REASONING
    },
    'background-tasks': {
      follow_up_generation: false
    }
  };

  // chatGPT does not like these params
  if (!store.connection.REASONING) {
    delete body.reasoning;
    delete body['background-tasks'];
  }

  store.textInput = '';

  const answerMessage = {
    role: USER_ROLE.AI,
    content: '...',
    loading: true,
    percent: 0
  };

  store.chatMessages.push(answerMessage);
  usage = -1;

  chat.value.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });

  const time = Date.now();
  Promise.all([
    ToastService.startToast().then(() => {
      store.startAndSubscribe(usage === -1 ? preEstimateUsage() : usage, (ant: AntSubscription) => {
        answerMessage.percent = Math.min(ant.percent, 100);
        loadingPercent.value = Math.min(ant.percent, 100);

        ToastService.progressToast(
          answerMessage.percent,
          answerMessage.content !== '...',
          ant.value
        );
        if (ant.percent >= 100 && usage > 0) {
          ToastService.energyToast(usage);
          usage = -1;
        }
      });
    }),
    axios
      .post(store.connection.BASE_URL + store.connection.ENDPOINT, body, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + props.token,
          'Cache-Control': 'no-cache, no-store'
        }
      })
      .then((result) => {
        const duration = Math.round((Date.now() - time) / 1000);
        usage = estimateEnergyUsage(
          result.data.usage.completion_tokens,
          result.data.usage.generation_tokens_per_second
        );
        store.setTarget(usage);

        console.log(
          'Energie verbraucht: ' +
            usage.toFixed(2) +
            ' Wh in ' +
            duration +
            ' Sekunden. \nDas benötigt eine Durchschnittsleistung von ' +
            Math.round((3600 * usage) / duration) +
            ' Watt.'
        );

        answerMessage.content = (result.data.choices[0].message.content as string).replaceAll(
          'ß',
          'ss'
        );
        answerMessage.loading = false;

        // we need to do this, or vue won't detect the update...
        store.chatMessages.pop();
        store.chatMessages.push(answerMessage);

        chat.value.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        emit('onAnswer', answerMessage);
      })
      .catch((e) => {
        console.error(e);
        ToastService.abort();
        emit('onError', JSON.stringify(e, null, 2));
      })
  ]);
}

/**
 * Sets a 1 second timeout in which we don't listen to hotkey inputs
 */
function inputting() {
  userInput.value = true;
  if (inputTimeout > 0) {
    window.clearTimeout(inputTimeout);
  }
  inputTimeout = window.setTimeout(() => {
    userInput.value = false;
  }, 1000);
}

function setPrompt(prompt: ExamplePrompt) {
  if (!store.chatMessages[store.chatMessages.length - 1]?.loading) {
    store.textInput = prompt[store.lang];
    usage = prompt.estimateFactor;
    store.setTarget(usage);
    (chatInput.value as any)?.focus();
  }
}
</script>

<template>
  <ul ref="chat-list" class="chat-list">
    <li
      v-for="(message, i) of store.getVisibleMessages()"
      :class="'message-bubble chat-' + message.role"
      :style="
        message.percent != undefined && message.percent < 100 && isLastMessage(i)
          ? loadingStyle
          : ''
      "
    >
      <div v-if="message.loading" class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div v-else v-html="md.render(message.content)" />
    </li>
    <li style="height: 10em; color: white; box-shadow: 0 0 0 white"></li>
  </ul>

  <PromptExamples
    @on-select-prompt="setPrompt"
    :user-inputting="userInput"
    v-if="
      !store.isPedalling() && store.getExamplePrompts().length > 0 && store.examplePromptsVisible
    "
  />

  <form action="#" ref="input-form" id="input-form">
    <input
      autofocus
      type="text"
      v-model="store.textInput"
      :placeholder="
        i18n(store.getExamplePrompts().length > 0 ? 'CHAT_PLACEHOLDER_PROMPTS' : 'CHAT_PLACEHOLDER')
      "
      @input="inputting"
      ref="chatInput"
    />
    <button
      @click="send"
      type="submit"
      :disabled="store.isPedalling()"
      :class="{ disabled: store.isPedalling() }"
    >
      <div v-if="store.isPedalling()" class="spinner-border" role="status">
        <span class="visually-hidden">...</span>
      </div>
      <span v-else>⬆︎</span>
    </button>
  </form>
</template>

<style scoped>
ul.chat-list {
  overflow: scroll;
  height: 100vh;
  margin-bottom: 0;
  padding: 1em;
  padding-top: 10em;
}
li:first-child {
  margin-top: 3em;
}
.message-bubble {
  box-shadow: 2px 2px 5px lightgray;
  list-style: none;
  padding: 0.25em 1em;
  border-radius: 1em;
  width: 75%;
  margin-top: 1em;
  transition:
    opacity 1.2s ease,
    filter 1.2s ease;
}
.chat-user {
  background-color: #64788b;
  color: #eff1f3;
  margin-left: 25%;
  border-bottom-right-radius: 0.5em;
}
.chat-assistant {
  background-color: #e0e4e8;
  color: #4b647d;
  margin-right: 25%;
  border-bottom-left-radius: 0.5em;
}
.chat-developer,
.chat-system {
  display: none;
}

.spinner-border {
  display: block;
  height: 1em;
  width: 1em;
  margin-left: auto;
  margin-right: auto;
}

input {
  border: 0;
  flex: 1;
  margin: 0.2em 1em;
}

input:focus {
  outline-width: 0;
  outline: none;
}

#input-form {
  box-shadow: 0px 0px 10px lightgray;
  display: flex;
  height: 2.4em;
  border-bottom-left-radius: 1.2em;
  border-top-left-radius: 1.2em;
  border-bottom-right-radius: 1.2em;
  border-top-right-radius: 1.2em;
}

button {
  border-bottom-left-radius: 1em;
  border-top-left-radius: 1em;
  border-bottom-right-radius: 1em;
  border-top-right-radius: 1em;
  background-color: #f5be41;
  color: white;
  margin: 0.2em;
  border: 0px;
  height: 2em;
  width: 2em;
}

button:hover {
  background-color: #f9b310;
  color: #697d91;
}

button.disabled {
  color: #697d91;
  opacity: 0.75;
  background-color: lightgray;
}

button .spinner-border {
  margin: 0 !important;
  width: 1.3em;
  height: 1.3em;
}

.usage {
  width: 100%;
  margin-top: 1em;
  display: block;
  text-align: center;
  color: darkgray;
}

.spinner-border {
  margin-top: 2em;
  margin-bottom: 2em;
}
.scroll-target {
  height: 4em;
  color: white;
}
</style>
